import { useTranslation } from 'react-i18next';
import {
  displayElementScoreRangeCategory,
  mapElementScoreToElementScoreRangeBreakpoint,
  isScorecardTermElement,
  ScorecardElementSymbol,
  isBeachModeElement,
} from '../../../../Scorecard';
import { EstateStatusItemState, RecurringFrequency } from '../../../../generated/graphql';
import {
  elementsColors,
  displayRate,
  displayRecurringMonetaryAmount,
  amountToRecurring,
  displayTerm,
} from '../../../../util';
import { LocalThemeContextProvider } from '../../../context';
import { TooltipContainer } from '../../Tooltip';
import { CalculationScorecardValues } from '../types';
import useScoreCalculationComponents from '../hooks/useScoreCalculationComponents';
import { Icon } from '../../Icon';
import clsx from 'clsx';
import { PresentationViewOptions } from '../../../';

export interface ScoreCalculationTooltipBodyProps {
  className?: string;
  score?: number | null;
  element: ScorecardElementSymbol;
  calculationScorecardValues: CalculationScorecardValues;
  beachMode?: boolean;
  viewOptions?: PresentationViewOptions;
  isFullscreen?: boolean;
}

export function ScoreCalculationTooltipBody({
  score,
  element,
  className,
  beachMode,
  calculationScorecardValues,
  viewOptions,
  isFullscreen,
}: ScoreCalculationTooltipBodyProps) {
  const { t: tScorecard } = useTranslation('scorecard');
  const beachModeElement = beachMode && isBeachModeElement(element);
  const { denominator, numerator, insuranceCoverageOwners } = useScoreCalculationComponents(
    element,
    calculationScorecardValues,
    viewOptions,
    isFullscreen
  );
  const estateStatusYesItems =
    calculationScorecardValues.estateStatusChecklistItems?.filter((item) => item.state === EstateStatusItemState.Yes) ??
    [];
  const estateStatusScoreIsMissing = element === 'Es' && score == null;

  return (
    <LocalThemeContextProvider paletteSeedColor={elementsColors[element]}>
      <TooltipContainer className={clsx(className, 'text-right text-xs')}>
        {beachModeElement ? (
          <div className="h-fit p-2 w-48">
            <div className="text-white text-right text-xs gap-0">{tScorecard('beach-mode-tool-tip')}</div>
          </div>
        ) : element === 'Ir' ? (
          insuranceCoverageOwners?.length ? (
            insuranceCoverageOwners?.map((owner, idx) => (
              <p key={idx}>
                <span className="text-darkMode-gray-light">{owner.name}</span>
                <span className="ml-1 text-white">{displayRate(owner.rate)}</span>
              </p>
            ))
          ) : (
            <span className="text-darkMode-gray-light">{tScorecard('ir-tooltip-no-coverage')}</span>
          )
        ) : element === 'Es' ? (
          <div className="w-56 text-left">
            <p className="text-darkMode-gray-light">{tScorecard('es-tooltip-checklist-score')}</p>
            {estateStatusYesItems.length ? (
              <div className="mt-2">
                <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-darkMode-gray-light/75">
                  {tScorecard('es-tooltip-yes-items-heading')}
                </p>
                <div className="mt-1 space-y-1">
                  {estateStatusYesItems.map((item) => (
                    <p key={item.id} className="flex items-start justify-between gap-3">
                      <span className="min-w-0 flex-1 truncate text-darkMode-gray-light">{item.label}</span>
                      <span className="text-white">{item.weightLabel}</span>
                    </p>
                  ))}
                </div>
              </div>
            ) : (
              <p className="mt-2 text-darkMode-gray-light">{tScorecard('es-tooltip-no-yes-items')}</p>
            )}
          </div>
        ) : (
          <>
            <p>
              <span className="text-darkMode-gray-light">{numerator?.name}</span>
              <span className="ml-1 text-white">
                {displayRecurringMonetaryAmount(amountToRecurring(numerator?.value, RecurringFrequency.Annually), {
                  notation: 'compact',
                  nullishIsDash: true,
                  maximumFractionDigits: 1,
                }).toLocaleLowerCase()}
              </span>
            </p>
            <p>
              <Icon icon="division" className="mr-1 inline text-palette-600" />
              <span className="text-darkMode-gray-light">{denominator?.name}</span>
              <span className="ml-1 text-white">
                {displayRecurringMonetaryAmount(amountToRecurring(denominator?.value, RecurringFrequency.Annually), {
                  notation: 'compact',
                  nullishIsDash: true,
                  maximumFractionDigits: 1,
                }).toLocaleLowerCase()}
              </span>
            </p>
          </>
        )}

        {!beachModeElement && (
          <p className="border-t-[1px] border-palette-600">
            {element !== 'Bt' && element !== 'Qt' && element !== 'Rt' && !estateStatusScoreIsMissing && (
              <span className="capitalize text-darkMode-gray-light">
                {displayElementScoreRangeCategory(
                  mapElementScoreToElementScoreRangeBreakpoint(element, score)?.scoreRangeCategory,
                  tScorecard
                )}
              </span>
            )}
            <span className={clsx('text-white', !estateStatusScoreIsMissing && 'ml-1')}>
              {estateStatusScoreIsMissing
                ? '––'
                : isScorecardTermElement(element)
                ? displayTerm(score)
                : displayRate(score)}
            </span>
          </p>
        )}
      </TooltipContainer>
    </LocalThemeContextProvider>
  );
}
